import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { focusRing } from "../../lib/focusRing";

const sizes = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required: it names the dialog for assistive tech. */
  title: ReactNode;
  description?: ReactNode;
  size?: keyof typeof sizes;
  /** Blocks Escape, overlay click and the close button while a mutation is in flight. */
  busy?: boolean;
  /** Action row, usually Cancel + primary Button. */
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * Modal dialog on Radix: focus trap, scroll lock, Escape and overlay dismissal,
 * focus restored to the trigger on close. Use ConfirmDialog for yes/no questions.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  busy = false,
  footer,
  children,
  className,
}: DialogProps) {
  const guard = (event: Event) => {
    if (busy) event.preventDefault();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-heading/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          onEscapeKeyDown={guard}
          onPointerDownOutside={guard}
          onInteractOutside={guard}
          className={cn(
            "fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 animate-scale-in",
            "flex max-h-[calc(100vh-2rem)] flex-col rounded-2xl bg-raised text-content shadow-lg outline-none",
            sizes[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-6">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-display text-lg font-semibold text-heading">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-subtle">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              disabled={busy}
              className={cn(
                "inline-flex size-9 shrink-0 items-center justify-center rounded-control text-subtle transition-colors hover:bg-overlay hover:text-heading disabled:opacity-50",
                focusRing,
              )}
            >
              <X className="size-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          {children ? (
            <div className="min-h-0 overflow-y-auto px-6 py-5 text-sm">{children}</div>
          ) : null}
          {footer ? (
            <div className="flex items-center justify-end gap-3 border-t border-edge-subtle px-6 py-4">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
